import React, { useState, useEffect } from 'react';
import validate from 'validate.js';
import { connect } from 'react-redux';
import * as actions from 'redux/actions/stockOrderActions';
import * as actions_stock from 'redux/actions/stockActions';
import * as actions_dashboard from 'redux/actions/dashboardActions';
import * as actions_event from 'redux/actions/eventActions';
import * as quoteActions from 'redux/actions/quoteActions';
import ListStockSelect from 'components/ListStockSelect';
import { Translate } from 'react-redux-i18n';
import InputDate from 'components/InputDate';

const schema = {
    symbol: {
        presence: { allowEmpty: false, message: 'is required' },
        length: { maximum: 300 },
    },
};

const FormDialog = (props) => {
    const [open, setOpen] = useState(false);
    const [dialogState, setDialogState] = useState({
        isValid: false,
        values: {
            symbol: '',
            quantity: 1,
            open_price: 0.01,
            market: 'BUY',
            open_at: new Date().toISOString().split('T')[0],
            id: props.user_id,
            name: '',
        },
        touched: {},
        errors: {},
    });

    const resetForm = () => ({
        isValid: false,
        values: {
            symbol: '',
            quantity: 1,
            open_price: 0.01,
            market: 'BUY',
            open_at: new Date().toISOString().split('T')[0],
            id: props.user_id,
            name: '',
        },
        touched: {},
        errors: {},
    });

    const handleClickOpen = () => {
        setOpen(true);
        setDialogState(resetForm());
    };

    const handleClose = () => {
        setOpen(false);
        props.resetList();
    };

    useEffect(() => {
        const errors = validate(dialogState.values, schema);
        setDialogState((prev) => ({
            ...prev,
            isValid: !errors,
            errors: errors || {},
        }));
    }, [dialogState.values]);

    useEffect(() => {
        if (!open || !props.item?.symbol) return;

        const symbol = String(props.item.symbol || '').toUpperCase();
        const lastPrice = Number(props.item.price || props.item.ask || props.item.current);

        setDialogState((prev) => {
            if (String(prev.values.symbol || '').toUpperCase() !== symbol) {
                return prev;
            }

            return {
                ...prev,
                values: {
                    ...prev.values,
                    name: props.item.name || props.item.symbol || prev.values.name || '',
                    open_price: !prev.touched.open_price && lastPrice > 0
                        ? Number(lastPrice.toFixed(2))
                        : prev.values.open_price,
                },
            };
        });
    }, [open, props.item]);

    useEffect(() => {
        if (!open) return;

        const symbol = String(dialogState.values.symbol || '').toUpperCase();
        if (!symbol || dialogState.touched.open_price) return;

        const quote = props.stockQuotes?.[symbol];
        const livePrice = Number(quote?.c);
        if (!livePrice || livePrice <= 0) return;

        setDialogState((prev) => {
            if (String(prev.values.symbol || '').toUpperCase() !== symbol || prev.touched.open_price) {
                return prev;
            }

            return {
                ...prev,
                values: {
                    ...prev.values,
                    open_price: Number(livePrice.toFixed(2)),
                },
            };
        });
    }, [open, dialogState.values.symbol, dialogState.touched.open_price, props.stockQuotes]);

    const handleChangeSymbol = (event) => {
        const value = event.target.value.trim();
        if (value !== dialogState.values.symbol) {
            setDialogState((prev) => ({
                ...prev,
                values: { ...prev.values, symbol: value, name: '' },
                touched: { ...prev.touched, symbol: true },
            }));
            props.searchList({ id: props.user_id, search: event.target.value }, props.token);
        } else {
            props.resetList();
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target || {};
        if (!name) return;

        setDialogState((prev) => ({
            ...prev,
            values: { ...prev.values, [name]: value },
            touched: { ...prev.touched, [name]: true },
        }));
    };

    const applySymbol = (symbol, name = '') => {
        const normalized = String(symbol || '').trim().toUpperCase();
        if (!normalized) return;

        setDialogState((prev) => ({
            ...prev,
            values: {
                ...prev.values,
                symbol: normalized,
                name: name || normalized,
            },
        }));
        props.loadItem({ id: props.user_id, symbol: normalized }, props.token);
        props.loadStockQuotes([normalized], props.user_id, props.token);
    };

    const onClickSelected = (item) => {
        if (!item?.symbol) return;
        applySymbol(item.symbol, item.name || item.symbol);
        props.resetList();
    };

    const handleSymbolBlur = () => {
        const symbol = String(dialogState.values.symbol || '').trim().toUpperCase();
        if (!symbol) return;
        applySymbol(symbol, dialogState.values.name || symbol);
    };

    const save = (share) => {
        if (!dialogState.values.symbol) return;
        const payload = share ? { ...dialogState.values, share: true } : dialogState.values;
        props.newItem(payload, props.token);
        props.loadListIndicator(props.user_id, props.token);
        props.suscribeStock(dialogState.values);
        setOpen(false);
    };

    const total = (
        (Number(dialogState.values.quantity) || 0) * (Number(dialogState.values.open_price) || 0)
    ).toFixed(2);

    return (
        <div>
            <button type="button" className="btn btn-primary" onClick={handleClickOpen}>
                <Translate value="new" />
            </button>
            {open && (
                <div className="dialog" role="presentation" onClick={handleClose}>
                    <div
                        className="dialog-content dialog-content--entry"
                        role="dialog"
                        aria-modal="true"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="dialog-title">Stock Entry</h2>
                        <p className="dialog-content-text">Enter stock trade details</p>

                        <div className="entry-form">
                            <div className="entry-form-section">
                                <div className="symbol-search-wrap">
                                    <div className="entry-field">
                                        <label htmlFor="stock-symbol">Symbol</label>
                                        <input
                                            autoFocus
                                            id="stock-symbol"
                                            name="symbol"
                                            type="text"
                                            required
                                            value={(dialogState.values.symbol || '').toUpperCase()}
                                            onChange={handleChangeSymbol}
                                            onBlur={handleSymbolBlur}
                                            autoComplete="off"
                                        />
                                    </div>
                                    {dialogState.values.symbol.length > 0 && !dialogState.values.name && (
                                        <ListStockSelect
                                            list={props.list}
                                            loading={props.loading}
                                            onClickSelected={onClickSelected}
                                        />
                                    )}
                                </div>
                                <div className="entry-field">
                                    <label htmlFor="stock-name">Company</label>
                                    <input
                                        id="stock-name"
                                        name="name"
                                        type="text"
                                        disabled
                                        value={dialogState.values.name || ''}
                                        autoComplete="off"
                                    />
                                </div>
                            </div>

                            <div className="entry-form-grid entry-form-grid--4 entry-form-grid--stock-entry">
                                <div className="entry-field">
                                    <label htmlFor="stock-quantity">Quantity</label>
                                    <input
                                        id="stock-quantity"
                                        name="quantity"
                                        type="number"
                                        min="1"
                                        required
                                        value={dialogState.values.quantity ?? ''}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="entry-field entry-field--compact">
                                    <label htmlFor="stock-price">Price</label>
                                    <input
                                        id="stock-price"
                                        name="open_price"
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        required
                                        value={dialogState.values.open_price}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="entry-field entry-field--compact">
                                    <label htmlFor="stock-market">Side</label>
                                    <select
                                        id="stock-market"
                                        name="market"
                                        required
                                        value={dialogState.values.market}
                                        onChange={handleChange}
                                    >
                                        <option value="BUY">Buy</option>
                                        <option value="SELL">Sell</option>
                                    </select>
                                </div>
                                <div className="entry-field">
                                    <label htmlFor="stock-open_at">Open date</label>
                                    <InputDate
                                        id="stock-open_at"
                                        name="open_at"
                                        required
                                        label=""
                                        value={dialogState.values.open_at}
                                        onChange={handleChange}
                                        min="1900-01-01"
                                        max={new Date().toISOString().split('T')[0]}
                                    />
                                </div>
                            </div>

                            <div className="entry-form-total">$ {total} USD</div>

                            <div className="dialog-actions">
                                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                                    <Translate value="cancel" />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={!dialogState.isValid}
                                    onClick={() => save(true)}
                                >
                                    <Translate value="save_share" />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={!dialogState.isValid}
                                    onClick={() => save(false)}
                                >
                                    <Translate value="save" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function mapStateToProps(reducer) {
    return {
        user_id: reducer.session.auth.id,
        token: reducer.session.auth.token,
        list: reducer.stock.list,
        item: reducer.stock.item,
        error: reducer.stock.error,
        loading: reducer.stock.loading,
        stockQuotes: reducer.event.stock,
    };
}

const mapDispatchToProps = (dispatch) => ({
    newItem: (params, token) => dispatch(actions.newItem({ params, token })),
    searchList: (params, token) => dispatch(actions_stock.searchList({ params, token })),
    loadItem: (params, token) =>
        dispatch(actions_stock.loadItem({ params, token })),
    loadStockQuotes: (symbols, user_id, token) =>
        dispatch(quoteActions.loadStockQuotes({ symbols, user_id, token })),
    resetList: () => dispatch(actions_stock.resetList({})),
    loadListIndicator: (user_id, token) =>
        dispatch(actions_dashboard.loadListIndicator({ user_id, token })),
    suscribeStock: (data) => dispatch(actions_event.suscribeStock(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FormDialog);
